// XLS/CSV parsing utilities
// Uses SheetJS (xlsx) library as specified in PRD
import * as XLSX from 'xlsx';

// Column mappings based on actual Appfolio export format (analyzed 2026-01-28)
// TODO: ASSUMPTION - These mappings match the current export format. Update if Appfolio changes.

export const COLUMN_MAPS = {
  workOrders: {
    headerRow: 17,
    columns: {
      id: 'Work Order ID',
      woNumber: 'Work Order Number',
      createdAt: 'Created At',
      status: 'Status',
      priority: 'Priority',
      property: 'Property',
      unit: 'Unit',
      tech: 'Assigned User',
      scheduledStart: 'Scheduled Start',
      resident: 'Primary Resident',
      description: 'Job Description',
      instructions: 'Instructions',
      createdBy: 'Created By',
      workDoneOn: 'Work Done On',
      vendorTrade: 'Vendor Trade',
      issue: 'Work Order Issue',
      propertyId: 'Property ID',
      unitAddress1: 'Unit Street Address 1',
      unitAddress2: 'Unit Street Address 2',
      workOrderType: 'Work Order Type',
      vendor: 'Vendor',
      unitId: 'Unit ID',
      residentPhone: 'Primary Resident Phone Number',
      completedOn: 'Completed On',
      canceledOn: 'Canceled On',
      tenantCharge: 'Tenant Total Charge Amount',
      requestingResident: 'Requesting Resident',
      surveyId: 'Survey ID',
    }
  },

  laborEntries: {
    headerRow: 9,
    // NOTE: Tech name appears in grouped rows - first column has name, rest is empty
    columns: {
      woNumber: 'Work Order Number',
      date: 'Date',
      property: 'Property',
      unit: 'Unit',
      startTime: 'Start Time',
      endTime: 'End Time',
      workedHours: 'Worked Hours',
      billableHours: 'Billable Hours',
      hoursDiff: 'Hours Difference',
      status: 'Work Order Status',
      description: 'Description',
      lastBillCreatedAt: 'Last Bill Created At',
      afterHours: 'Marked After Hours',
      timerStart: 'Timer Start',
      timerStop: 'Timer Stop',
      propertyId: 'Property ID',
      unitId: 'Unit ID',
      woId: 'Work Order ID',
    }
  },

  inventory: {
    headerRow: 9,
    columns: {
      property: 'Property',
      unit: 'Unit',
      itemName: 'Item Name',
      quantity: 'Quantity',
      cost: 'Cost',
      salePrice: 'Sale Price',
      category: 'Category',
      issue: 'Work Order Issue',
      woId: 'Work Order ID',
      location: 'Inventory Location',
      woNumber: 'Work Order Number',
      propertyId: 'Property ID',
      propertyAddress: 'Property Street Address 1',
      unitId: 'Unit ID',
    }
  },

  surveys: {
    headerRow: 8,
    columns: {
      respondent: 'Respondent',
      property: 'Property',
      satisfaction: 'How satisfied are you with the overall experience?',
      resolved: 'Has the issue been resolved completely?',
      timely: 'Was the work completed in a timely manner?',
      feedback: 'Any additional feedback?',
      dateSent: 'Date Sent',
      dateReceived: 'Date Received',
      surveyId: 'Survey ID',
      propertyId: 'Property ID',
      woNumber: 'Work Order Number',
      woId: 'Work Order ID',
      serviceRequestId: 'Service Request ID',
      jobDescription: 'Job Description',
      tech: 'Assigned User',
    }
  },

  units: {
    headerRow: 6,
    columns: {
      name: 'Unit Name',
      marketRent: 'Market Rent',
      deposit: 'Default Deposit',
      sqft: 'Sqft',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      tags: 'Tags',
      revenue: 'Revenue',
      description: 'Description',
      propertyId: 'Property ID',
      rentStatus: 'Rent Status',
      unitId: 'Unit ID',
    }
  },
};

/**
 * Parse an Excel workbook file with header row offset
 * @param {ArrayBuffer|Blob|File} file - The file to parse
 * @param {number} headerRow - Row index where headers are (0-based)
 * @returns {Array<Object>} - Array of row objects
 */
export const parseWorkbook = async (file, headerRow = 0) => {
  try {
    let data;

    if (file instanceof ArrayBuffer) {
      data = file;
    } else if (file instanceof Blob || file instanceof File) {
      data = await file.arrayBuffer();
    } else {
      throw new Error('Invalid file type. Expected ArrayBuffer, Blob, or File.');
    }

    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];

    if (!sheetName) {
      throw new Error('No sheets found in workbook');
    }

    const sheet = workbook.Sheets[sheetName];

    // Get raw data as array of arrays
    const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    // Find header row (use specified or auto-detect as row with most columns)
    let actualHeaderRow = headerRow;
    if (headerRow === 0) {
      let maxCols = 0;
      for (let i = 0; i < Math.min(25, rawData.length); i++) {
        if (rawData[i] && rawData[i].length > maxCols) {
          maxCols = rawData[i].length;
          actualHeaderRow = i;
        }
      }
    }

    const headers = rawData[actualHeaderRow];
    if (!headers) {
      throw new Error('Could not find header row');
    }

    // Convert to array of objects starting from row after headers
    const results = [];
    for (let i = actualHeaderRow + 1; i < rawData.length; i++) {
      const row = rawData[i];
      if (!row || row.length === 0) continue;

      // Skip rows where first cell matches a header pattern (metadata rows)
      const firstCell = String(row[0] || '').trim();
      if (firstCell.includes(':') && firstCell.length < 100) continue;

      const obj = {};
      let hasData = false;

      headers.forEach((header, colIndex) => {
        if (header && row[colIndex] !== undefined && row[colIndex] !== null && row[colIndex] !== '') {
          obj[header] = row[colIndex];
          hasData = true;
        }
      });

      if (hasData) {
        results.push(obj);
      }
    }

    return results;
  } catch (error) {
    console.error('Error parsing workbook:', error);
    throw new Error(`Failed to parse file: ${error.message}`);
  }
};

/**
 * Parse work orders from the Appfolio export
 */
export const parseWorkOrders = async (file) => {
  const { headerRow, columns } = COLUMN_MAPS.workOrders;
  const rawData = await parseWorkbook(file, headerRow);

  return rawData.map(row => ({
    id: row[columns.woNumber] || row[columns.id],
    woNumber: row[columns.woNumber],
    createdDate: parseExcelDate(row[columns.createdAt]),
    status: normalizeStatus(row[columns.status]),
    priority: row[columns.priority]?.toLowerCase() || 'normal',
    property: cleanPropertyName(row[columns.property]),
    unit: row[columns.unit] || '',
    tech: row[columns.tech] || '',
    scheduledStart: parseExcelDate(row[columns.scheduledStart]),
    resident: row[columns.resident] || '',
    description: row[columns.description] || '',
    instructions: row[columns.instructions] || '',
    issue: row[columns.issue] || '',
    propertyId: row[columns.propertyId],
    unitId: row[columns.unitId],
    completedOn: parseExcelDate(row[columns.completedOn]),
    workOrderType: row[columns.workOrderType] || '',
    vendor: row[columns.vendor] || '',
  })).filter(wo => wo.id); // Filter out empty rows
};

/**
 * Parse labor entries from the Appfolio export
 * NOTE: Tech name appears in grouped rows
 */
export const parseLaborEntries = async (file) => {
  const { headerRow, columns } = COLUMN_MAPS.laborEntries;
  const rawData = await parseWorkbook(file, headerRow);

  const results = [];
  let currentTech = '';

  for (const row of rawData) {
    const woNumber = row[columns.woNumber];

    // Check if this is a tech name row (first column has value, rest are empty)
    const hasWoData = row[columns.date] || row[columns.startTime] || row[columns.workedHours];

    if (woNumber && !hasWoData) {
      // This is a tech name row
      currentTech = woNumber;
      continue;
    }

    if (!woNumber || !hasWoData) continue;

    results.push({
      id: `${woNumber}-${row[columns.timerStart] || results.length}`,
      techName: currentTech,
      woId: row[columns.woId],
      woNumber: woNumber,
      date: parseExcelDate(row[columns.date]),
      property: cleanPropertyName(row[columns.property]),
      unit: row[columns.unit] || '',
      startTime: parseExcelDateTime(row[columns.startTime]),
      stopTime: parseExcelDateTime(row[columns.endTime]),
      hours: parseFloat(row[columns.workedHours]) || 0,
      billableHours: parseFloat(row[columns.billableHours]) || 0,
      description: row[columns.description] || '',
      status: row[columns.status] || '',
      afterHours: row[columns.afterHours] === 'Yes',
      propertyId: row[columns.propertyId],
      unitId: row[columns.unitId],
    });
  }

  return results;
};

/**
 * Parse inventory/parts usage from the Appfolio export
 */
export const parseInventory = async (file) => {
  const { headerRow, columns } = COLUMN_MAPS.inventory;
  const rawData = await parseWorkbook(file, headerRow);

  return rawData.map(row => ({
    id: `${row[columns.woId]}-${row[columns.itemName]}`,
    woId: row[columns.woId],
    woNumber: row[columns.woNumber],
    property: cleanPropertyName(row[columns.property]),
    unit: row[columns.unit] || '',
    itemName: row[columns.itemName] || '',
    quantity: parseInt(row[columns.quantity]) || 1,
    cost: parseFloat(row[columns.cost]) || 0,
    salePrice: parseFloat(row[columns.salePrice]) || 0,
    category: row[columns.category] || '',
    issue: row[columns.issue] || '',
    location: row[columns.location] || '',
    propertyId: row[columns.propertyId],
    unitId: row[columns.unitId],
  })).filter(item => item.woId);
};

/**
 * Parse survey responses from the Appfolio export
 */
export const parseSurveys = async (file) => {
  const { headerRow, columns } = COLUMN_MAPS.surveys;
  const rawData = await parseWorkbook(file, headerRow);

  return rawData.map(row => ({
    id: row[columns.surveyId],
    respondent: row[columns.respondent] || '',
    property: cleanPropertyName(row[columns.property]),
    satisfaction: parseInt(row[columns.satisfaction]) || null,
    resolved: row[columns.resolved] === 'Yes',
    timely: row[columns.timely] === 'Yes',
    feedback: row[columns.feedback] || '',
    dateSent: parseExcelDate(row[columns.dateSent]),
    dateReceived: parseExcelDate(row[columns.dateReceived]),
    woId: row[columns.woId],
    woNumber: row[columns.woNumber],
    tech: row[columns.tech] || '',
    propertyId: row[columns.propertyId],
  })).filter(s => s.satisfaction !== null);
};

/**
 * Parse unit directory from the Appfolio export
 */
export const parseUnits = async (file) => {
  const { headerRow, columns } = COLUMN_MAPS.units;
  const rawData = await parseWorkbook(file, headerRow);

  return rawData.map(row => ({
    id: row[columns.unitId] || row[columns.name],
    name: row[columns.name] || '',
    marketRent: parseFloat(row[columns.marketRent]) || 0,
    deposit: parseFloat(row[columns.deposit]) || 0,
    sqft: parseInt(row[columns.sqft]) || 0,
    bedrooms: parseInt(row[columns.bedrooms]) || 0,
    bathrooms: parseFloat(row[columns.bathrooms]) || 0,
    tags: row[columns.tags] || '',
    propertyId: row[columns.propertyId],
    unitId: row[columns.unitId],
    // Extract property name from unit name (format: "Unit Name Property Address")
    property: extractPropertyFromUnitName(row[columns.name]),
  })).filter(u => u.name && u.unitId);
};

/**
 * Extract unique technicians from labor entries
 */
export const extractTechnicians = (laborEntries) => {
  const techMap = new Map();

  laborEntries.forEach(entry => {
    if (entry.techName && !techMap.has(entry.techName)) {
      techMap.set(entry.techName, {
        id: entry.techName.toLowerCase().replace(/\s+/g, '-'),
        name: entry.techName,
        email: `${entry.techName.toLowerCase().replace(/\s+/g, '.')}@snshn.com`, // TODO: ASSUMPTION - email format
      });
    }
  });

  return Array.from(techMap.values());
};

// === Helper Functions ===

/**
 * Parse Excel serial date to JS Date
 */
export const parseExcelDate = (value) => {
  if (!value) return null;

  // Already a date string
  if (typeof value === 'string' && value.includes('/')) {
    return new Date(value).toISOString().split('T')[0];
  }

  // Excel serial number (days since 1899-12-30)
  if (typeof value === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + Math.floor(value) * 86400000);
    return date.toISOString().split('T')[0];
  }

  return null;
};

/**
 * Parse Excel serial datetime to JS Date
 */
export const parseExcelDateTime = (value) => {
  if (!value) return null;

  if (typeof value === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + value * 86400000);
    return date.toISOString();
  }

  return null;
};

/**
 * Clean property name (remove trailing dash/hyphen)
 */
export const cleanPropertyName = (name) => {
  if (!name) return '';
  return String(name).replace(/\s*-\s*$/, '').trim();
};

/**
 * Extract property from unit name
 */
export const extractPropertyFromUnitName = (unitName) => {
  if (!unitName) return '';
  // Unit names often include city/state - extract the address part
  const match = unitName.match(/^(.+?)\s+(Milwaukee|West Allis|Wauwatosa)/i);
  return match ? match[1].trim() : unitName.split(' ').slice(0, 4).join(' ');
};

/**
 * Normalize WO status to app format
 */
export const normalizeStatus = (status) => {
  if (!status) return 'open';
  const s = status.toLowerCase();
  if (s.includes('completed') || s.includes('done')) return 'closed';
  if (s.includes('cancel')) return 'cancelled';
  if (s.includes('waiting') || s.includes('scheduled')) return 'scheduled';
  return 'open';
};

/**
 * Parse a CSV file
 */
export const parseCSV = async (file) => {
  try {
    let content;

    if (typeof file === 'string') {
      content = file;
    } else if (file instanceof ArrayBuffer) {
      content = new TextDecoder().decode(file);
    } else if (file instanceof Blob || file instanceof File) {
      content = await file.text();
    } else {
      throw new Error('Invalid file type');
    }

    const workbook = XLSX.read(content, { type: 'string' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    return XLSX.utils.sheet_to_json(sheet, {
      defval: '',
      raw: false,
    });
  } catch (error) {
    console.error('Error parsing CSV:', error);
    throw new Error(`Failed to parse CSV: ${error.message}`);
  }
};

/**
 * Parse bank charges from Chase CSV export
 * Columns: Card, Transaction Date, Post Date, Description, Category, Type, Amount, Memo
 */
export const parseBankCharges = async (file) => {
  const rawData = await parseCSV(file);

  return rawData
    .filter(row => {
      // Only include purchases (negative amounts, Sale type)
      const amount = parseFloat(row['Amount']) || 0;
      const type = row['Type'] || '';
      return amount < 0 && type === 'Sale';
    })
    .map((row, index) => ({
      id: `charge-${row['Card']}-${row['Transaction Date']}-${index}`,
      cardLast4: String(row['Card']).trim(), // Ensure string for consistent matching
      transactionDate: row['Transaction Date'],
      postDate: row['Post Date'],
      vendor: cleanVendorName(row['Description']),
      vendorRaw: row['Description'],
      category: row['Category'],
      type: row['Type'],
      amount: Math.abs(parseFloat(row['Amount']) || 0),
      memo: row['Memo'] || '',
      matched: false, // Will be set to true when receipt is uploaded
    }));
};

/**
 * Clean up vendor name for display
 */
export const cleanVendorName = (name) => {
  if (!name) return '';
  return name
    .replace(/&amp;/g, '&')
    .replace(/\s+#\d+/g, '') // Remove store numbers like #4912
    .replace(/\s+\d{4,}/g, '') // Remove long numbers
    .replace(/\*.*$/, '') // Remove everything after asterisk
    .trim();
};

/**
 * Parse cards CSV - maps card last-4 digits to technician names
 * Columns: card, Who Physically has it, Whose name is on card, CC Agreement on file?, (status), number, email
 */
export const parseCards = async (file) => {
  const rawData = await parseCSV(file);

  return rawData
    .filter(row => {
      // Filter out rows with no card number or marked as closed/deactivated
      const card = String(row['card'] || '').trim();
      const status = String(row['__EMPTY_1'] || row[''] || '').toLowerCase();
      return card && card.match(/^\d+$/) && !status.includes('closed') && !status.includes('deactivated');
    })
    .map(row => ({
      cardLast4: String(row['card']).trim(),
      holderName: row['Who Physically has it'] || '',
      cardName: row['Whose name is on card'] || '',
      hasAgreement: String(row['CC Agreement on file?'] || '').toLowerCase() === 'yes',
      phone: row['number'] || '',
      email: row['email'] || '',
      status: String(row['__EMPTY_1'] || '').toLowerCase().includes('locked') ? 'locked' : 'active',
    }));
};

/**
 * Parse lock codes CSV - unit lock codes and common lock codes
 * Row 1 is warning text, Row 2 is headers
 * Columns: Property, Property ID, Unit Address, Unit ID, Code of Unit Lock(s), Code of Common Lock(s), Lock Notes, Code of lockbox
 */
export const parseLockCodes = async (file) => {
  try {
    let content;

    if (typeof file === 'string') {
      content = file;
    } else if (file instanceof ArrayBuffer) {
      content = new TextDecoder().decode(file);
    } else if (file instanceof Blob || file instanceof File) {
      content = await file.text();
    } else {
      throw new Error('Invalid file type');
    }

    const workbook = XLSX.read(content, { type: 'string' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    // Get raw data as array of arrays
    const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    // Row 0 is warning text, Row 1 is headers
    const headers = rawData[1];
    if (!headers) {
      throw new Error('Could not find header row');
    }

    // Find column indices
    const colIndex = {};
    headers.forEach((h, i) => {
      if (h) colIndex[h] = i;
    });

    // Parse data starting from row 2
    const results = [];
    for (let i = 2; i < rawData.length; i++) {
      const row = rawData[i];
      if (!row || row.length === 0) continue;

      const unitId = row[colIndex['Unit ID']];
      const unitLockCode = row[colIndex['Code of Unit Lock(s)']];
      const commonLockCode = row[colIndex['Code of Common Lock(s)']];

      // Only include rows that have a unit ID and at least one lock code
      if (!unitId) continue;
      if (!unitLockCode && !commonLockCode) continue;

      results.push({
        id: `lock-${unitId}`,
        propertyId: row[colIndex['Property ID']] || '',
        property: row[colIndex['Property']] || '',
        unitId: String(unitId),
        unitAddress: row[colIndex['Unit Address']] || '',
        unitLockCode: String(unitLockCode || '').trim().toUpperCase(),
        commonLockCode: String(commonLockCode || '').trim().toUpperCase(),
        lockNotes: row[colIndex['Lock Notes']] || '',
        lockboxCode: row[colIndex['Code of lockbox']] || '',
      });
    }

    return results;
  } catch (error) {
    console.error('Error parsing lock codes:', error);
    throw new Error(`Failed to parse lock codes: ${error.message}`);
  }
};

export default {
  parseWorkbook,
  parseWorkOrders,
  parseLaborEntries,
  parseInventory,
  parseSurveys,
  parseUnits,
  extractTechnicians,
  parseCSV,
  parseBankCharges,
  parseCards,
  parseLockCodes,
  parseExcelDate,
  parseExcelDateTime,
  COLUMN_MAPS,
};
